import React, { useCallback } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { Skeleton, Typography } from '@mui/material';
import { StopCircle } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { AlineAlert, AlineButton } from 'src/components/generic';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { Apollo } from 'src/gql/generated';
import { useGetPlaybook } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { PlaybookHeader } from './PlaybookHeader';
import { RuleDetailsPanel } from './RuleDetailsPanel';
import { RulesSection } from './RulesSection';
import type { PlaybookGenerationProgress } from './use-playbook-generation-progress';
import {
  getIsGenerationInFlight,
  usePlaybookGenerationProgress,
} from './use-playbook-generation-progress';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexGrow: 1,
    minHeight: 0,
  },
  // Owns the divider so the line spans the full column height, not just the
  // panel's content, and stays put while the panel scrolls inside it.
  detailsColumn: {
    borderLeft: `1px solid ${theme.appColors.rule}`,
    // A column so the panel inside can stretch to the full height, making the
    // space below the last position part of the panel's hover area.
    display: 'flex',
    flexBasis: '40%',
    flexDirection: 'column',
    flexShrink: 0,
    maxWidth: 480,
    minWidth: 0,
    overflowY: 'auto',
  },
  editorColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(3),
    minWidth: 0,
    overflowY: 'auto',
    padding: theme.spacing(2.5, 3, 4),
  },
  generationAlert: {
    '& .MuiAlert-action': {
      alignItems: 'center',
      marginRight: 0,
    },
    '& .MuiAlert-icon': {
      alignItems: 'center',
      padding: 0,
    },
  },
  message: {
    padding: theme.spacing(3),
  },
  skeletonCard: {
    borderRadius: 8,
  },
  skeletonHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  skeletonRules: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  skeletonTitleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

export function PlaybookDetailPage(): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { playbookId, ruleId } = useParams();
  const { loading, playbook, refetch } = useGetPlaybook(playbookId ?? '', { skip: !playbookId });
  const generationJob = playbook?.generationJob ?? null;
  const isGenerating = getIsGenerationInFlight(generationJob);
  const selectedRuleId = ruleId ?? null;
  /**
   * Replace rather than push: stepping through rules is one visit to the
   * playbook, not a history entry per rule.
   */
  const selectRule = useCallback(
    (nextRuleId: UUID | null): void => {
      if (!playbookId) return;
      navigate(
        nextRuleId
          ? link.PlaybooksDetailRule({ playbookId, ruleId: nextRuleId })
          : link.PlaybooksDetail({ playbookId }),
        { replace: true }
      );
    },
    [navigate, playbookId]
  );

  usePlaybookGenerationProgress({
    generationJobId: generationJob?.id ?? null,
    isInFlight: isGenerating,
    onGone: refetch,
    playbookId: playbookId ?? null,
  });

  if (loading && !playbook) return <PlaybookDetailSkeleton />;
  if (!playbook) {
    return (
      <Typography align="center" className={classes.message} variant="h1">
        Playbook not found.
      </Typography>
    );
  }

  const selectedRule =
    playbook.categories
      .flatMap(category => category.rules)
      .find(rule => rule.id === selectedRuleId) ?? null;

  return (
    <div className={classes.container} data-cy={Select.playbooks.detailPage}>
      <Helmet>
        <title>{playbook.title || 'Untitled playbook'}</title>
      </Helmet>
      <div className={classes.editorColumn}>
        {isGenerating && <GeneratingBanner generationJob={generationJob} onStopped={refetch} />}
        {Boolean(generationJob?.failedAt) && (
          <GenerationFailedBanner failedReason={generationJob?.failedReason ?? null} />
        )}
        <PlaybookHeader disabled={isGenerating} playbook={playbook} />
        <RulesSection
          disabled={isGenerating}
          isGenerating={isGenerating}
          openCategoryId={generationJob?.openCategory?.id ?? null}
          playbook={playbook}
          selectedRuleId={selectedRuleId}
          onSelectRule={selectRule}
        />
      </div>
      {selectedRule && (
        <div className={classes.detailsColumn}>
          {/* Keyed so selecting another rule remounts the panel — otherwise the
              autofocus on an empty rule only fires the first time it opens. */}
          <RuleDetailsPanel key={selectedRule.id} disabled={isGenerating} rule={selectedRule} />
        </div>
      )}
    </div>
  );
}

/**
 * Says the playbook is still being written, so an author who opens it mid-run
 * knows the categories below are what has arrived so far rather than all there
 * is. The open category shows one rule skeleton; a generic card stands in between
 * categories.
 *
 * Stop ends the run and keeps whatever it has written. The playbook stays as
 * it is at that moment, with the categories that landed and none of the ones
 * that did not.
 */
function GeneratingBanner({
  generationJob,
  onStopped,
}: {
  generationJob: PlaybookGenerationProgress;
  onStopped: () => void;
}): JSX.Element {
  const { classes } = useStyles();
  const [cancelGeneration, { loading: stopping }] = Apollo.useCancelPlaybookGenerationJobMutation();
  const stopGeneration = useCallback(async () => {
    await cancelGeneration({ variables: { jobId: generationJob.id } });
    onStopped();
  }, [cancelGeneration, generationJob.id, onStopped]);

  return (
    <AlineAlert
      action={
        <AlineButton
          color="primary"
          data-cy={Select.playbooks.stopGenerationButton}
          loading={stopping}
          size="small"
          startIcon={<StopCircle size={16} />}
          variant="outlined"
          onClick={() => void stopGeneration()}
        >
          Stop
        </AlineButton>
      }
      className={classes.generationAlert}
      icon={<PulseIcon variant={PulseIconVariant.LoadingPrimary} />}
      severity="info"
      variant="outlined"
    >
      AI is generating your playbook...
    </AlineAlert>
  );
}

/** Says the run that was writing this playbook stopped, and why. */
function GenerationFailedBanner({ failedReason }: { failedReason: string | null }): JSX.Element {
  const { classes } = useStyles();

  return (
    <AlineAlert className={classes.generationAlert} severity="error" variant="outlined">
      {failedReason || 'AI generation of this playbook failed.'}
    </AlineAlert>
  );
}

/**
 * Stands in for the title, description and category cards while the playbook
 * loads, so the page keeps its shape instead of appearing all at once. The
 * details column is left out: which rule is open is only known once the
 * playbook has arrived.
 */
function PlaybookDetailSkeleton(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.editorColumn}>
        <div className={classes.skeletonHeader}>
          <div className={classes.skeletonTitleRow}>
            <Skeleton height={24} variant="circular" width={24} />
            <Skeleton height={32} variant="text" width="40%" />
          </div>
          <Skeleton height={20} variant="text" width="60%" />
        </div>
        <div className={classes.skeletonRules}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={index}
              className={classes.skeletonCard}
              height={132}
              variant="rectangular"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
